import { Injectable } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { Observable, Subject, of } from 'rxjs';

import { UpdateAllPointIds, UpdatePointIds } from '../workspace/workspace.actions';
import {
  ClearPoints,
  DeletePoint,
  DeletePoints,
  SetAllPoints,
  SetPoints,
  UpdatePoint,
} from './points.actions';

import { TWorkspacesById } from '../workspace/workspace.model';
import { TPoint, TPointListByWorkspace, TPointsByWorkspace } from './points.model';

import { ActiveService } from '../../services/active/active.service';
import { ResponseErrorService } from '../errors/response-error.service';
import { WorkspaceService } from '../workspace/workspace.service';
import { PointsGenerateService } from './point-generate/points-generate.service';
import { PointsService } from './points.service';

import { PointsApiProviderService } from '@core/api';
import { TPointResponse } from '@project/view-models';
import cloneDeep from 'lodash/cloneDeep';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { EStore } from '../../shared/enums/store.enum';
import { CustomTableService } from '../site/site-table/custom-table/custom-table.service';
import { RemindersService } from '../reminders/reminders.service';
import { PointsPaginationService } from './points-pagination.service';
import { removeSelectedPoint } from './selected-points';

@Injectable({
  providedIn: 'root',
})
export class PointsFetchingService {
  public readonly pointsRemoved$ = new Subject<string[]>();

  private points$: Observable<TPointResponse[]>;
  private points: TPointsByWorkspace;
  private pointsByWorkspace$: Observable<TPointsByWorkspace>;

  private pointsFetched = false;
  private pointsFetching = false;

  private workspaces$: Observable<TWorkspacesById>;
  private workspaces: TWorkspacesById;

  constructor(
    private store: Store<{
      points: TPointsByWorkspace;
      workspaces: TWorkspacesById;
    }>,
    private pointsGenerateService: PointsGenerateService,
    private responseErrorService: ResponseErrorService,
    private workspaceService: WorkspaceService,
    private pointsService: PointsService,
    private activeService: ActiveService,
    private pointsApiProviderService: PointsApiProviderService,
    private pointsPaginationService: PointsPaginationService,
    private remindersService: RemindersService,
    private customTableService: CustomTableService,
  ) {
    this.workspaces$ = this.store.pipe(select(EStore.WORKSPACES));
    this.pointsByWorkspace$ = this.store.pipe(select(EStore.POINTS));

    this.pointsByWorkspace$.subscribe((pointsByWorkspace) => {
      this.points = pointsByWorkspace;
    });

    this.workspaces$.subscribe((workspaces) => {
      this.workspaces = workspaces;
    });
  }

  fetchPoint(_id: string): Observable<TPoint> {
    return this.pointsApiProviderService.fetchPoint(_id).pipe(
      map((response) => {
        const point = this.pointsGenerateService.generatePoint(response);

        this.store.dispatch(
          new UpdatePoint({
            workspaceId: point.workspaceRef.id,
            point,
          }),
        );

        return point;
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to fetch point'),
      ),
    );
  }

  fetchPoints(
    workspaceId: string,
    {
      forceFetch = false,
    }: {
      forceFetch?: boolean;
    } = {},
  ): Observable<TPointResponse[]> {
    if (!this.pointsFetching || forceFetch) {
      this.pointsFetching = true;

      this.points$ = this.pointsPaginationService.fetchPoints(workspaceId).pipe(
        tap((response) => this.updatePointsIdsStore(workspaceId, response)),
        shareReplay(),
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to fetch points'),
        ),
        finalize(() => {
          this.pointsFetching = false;
        }),
      );
    }

    return this.points$;
  }

  updatePointsIdsStore(_workspaceId: string, _response: TPointResponse[]): void {
    const points = _response ? _response : [];

    this.store.dispatch(
      new SetPoints({
        workspaceId: _workspaceId,
        points,
      }),
    );

    const pointIds = points.map(({ _id }) => _id);

    this.store.dispatch(
      new UpdatePointIds({
        workspaceId: _workspaceId,
        pointIds,
      }),
    );
  }

  fetchPointsByIds(pointList: string[]): Observable<TPointResponse[]> {
    if (!pointList.length) {
      return of(null);
    }
    return this.pointsApiProviderService.fetchPointsById(pointList).pipe(
      tap((response) => {
        this.storePointsData(response);
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to fetch points by IDs'),
      ),
    );
  }

  clearPoints(): void {
    this.pointsFetching = false;
    this.pointsFetched = false;

    this.store.dispatch(new ClearPoints());
  }

  deletePoint(_id: string): Observable<null> {
    const workspaceId = this.activeService.getActiveWorkspaceId();

    return this.pointsApiProviderService.deletePoint(_id).pipe(
      tap(() => {
        removeSelectedPoint(_id);
        this.remindersService.clearData();

        this.store.dispatch(
          new DeletePoint({
            workspaceId,
            _id,
          }),
        );

        this.customTableService.clearGroupSizes();

        this.pointsRemoved$.next([_id]);
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to delete point'),
      ),
    );
  }

  deletePoints(pointIds: string[]): Observable<void> {
    const workspaceId = this.activeService.getActiveWorkspaceId();

    return this.pointsApiProviderService.deletePoints(pointIds).pipe(
      tap(() => {
        pointIds.forEach((id) => removeSelectedPoint(id));

        this.store.dispatch(
          new DeletePoints({
            workspaceId,
            pointIds,
          }),
        );

        this.customTableService.clearGroupSizes();

        this.pointsRemoved$.next(pointIds);
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to delete points'),
      ),
    );
  }

  private storePointsData(response: TPointResponse[]): void {
    const points = response;

    const workspaces = this.workspaceService.getWorkspaces();
    const pointsByWorkspace: TPointListByWorkspace = {};
    const pointIdsByWorkspace = {};
    const cachedPoints = cloneDeep(this.points);

    Object.keys(workspaces).forEach((workspaceId) => {
      pointsByWorkspace[workspaceId] = cachedPoints[workspaceId]?.entities || [];
      pointIdsByWorkspace[workspaceId] = cachedPoints[workspaceId]?.sortedPointIds || [];
    });

    points.forEach((point) => {
      if (!pointsByWorkspace[point.workspaceRef.id]) {
        pointsByWorkspace[point.workspaceRef.id] = [];
      }

      if (!pointIdsByWorkspace[point.workspaceRef.id]) {
        pointIdsByWorkspace[point.workspaceRef.id] = [];
      }

      const pointExists = pointsByWorkspace[point.workspaceRef.id].find(
        (pointInList) => pointInList._id === point._id,
      );
      if (!pointExists) {
        pointsByWorkspace[point.workspaceRef.id].push(point);
      }

      if (!pointIdsByWorkspace[point.workspaceRef.id].includes(point._id)) {
        pointIdsByWorkspace[point.workspaceRef.id].push(point._id);
      }
    });

    this.store.dispatch(
      new SetAllPoints({
        pointsByWorkspace,
      }),
    );

    this.store.dispatch(
      new UpdateAllPointIds({
        pointIdsByWorkspace,
      }),
    );
  }
}
